import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  ripple: boolean;
  menuMode: string;
  scale: number;
}

interface LayoutState {
  staticMenuDesktopInactive: boolean;
  overlayMenuActive: boolean;
  profileSidebarVisible: boolean;
  configSidebarVisible: boolean;
  staticMenuMobileActive: boolean;
  menuHoverActive: boolean;
}

const STORAGE_RAIL_ID = 'acan.nav.activeRailId';
const STORAGE_PANEL_PINNED = 'acan.nav.detailPanelPinned';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  config: AppConfig = {
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'light',
    theme: 'mdc-light-indigo',
    scale: 14,
  };

  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  };

  private configUpdate = new Subject<AppConfig>();

  private overlayOpen = new Subject<any>();
  overlay: BehaviorSubject<Boolean> = new BehaviorSubject<Boolean>(false);

  /** @deprecated kept for any leftover callers; dual-nav uses detailPanelOpen */
  iconsMenu: BehaviorSubject<Boolean> = new BehaviorSubject<Boolean>(false);

  /** Route / click committed rail section */
  activeRailId$ = new BehaviorSubject<string | null>(this.readStoredRailId());
  /** Hover preview section (not persisted) */
  previewRailId$ = new BehaviorSubject<string | null>(null);
  /** Inline panel open (pinned or mobile drawer) */
  detailPanelOpen$ = new BehaviorSubject<boolean>(this.readStoredPinned());
  /** When true, inline panel stays open; hover uses overlay when false */
  detailPanelPinned = this.readStoredPinned();

  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  onMenuToggle() {
    this.overlay.next(true);
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;
      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive =
        !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive = !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.clearPreview();
        this.setDetailPanelOpen(true, false);
        this.overlayOpen.next(null);
      } else if (!this.detailPanelPinned) {
        this.clearPreview();
        this.setDetailPanelOpen(false, false);
      }
    }
  }

  getMenuDesktopActiveState(): boolean {
    return this.state.staticMenuDesktopInactive;
  }

  showProfileSidebar() {
    this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
    if (this.state.profileSidebarVisible) {
      this.overlayOpen.next(null);
    }
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config.menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this.configUpdate.next(this.config);
  }

  /** Topbar toggle: pin/unpin the inline detail panel. */
  toggleDetailPanel(): void {
    const next = !this.detailPanelPinned;
    this.clearPreview();
    this.detailPanelPinned = next;
    this.setDetailPanelOpen(next, true);
    this.persistPinned(next);
  }

  /**
   * Hover preview: show section shortcuts.
   * Does not change route-active rail id. When collapsed, flyout overlays content.
   */
  previewRail(id: string): void {
    this.cancelHoverClose();
    this.previewRailId$.next(id);
  }

  /** Keep flyout/preview open while pointer is over rail or panel. */
  keepPreviewOpen(): void {
    this.cancelHoverClose();
  }

  /**
   * After pointer leaves nav: clear hover preview.
   * Pinned panel stays open on the route-active section.
   */
  schedulePreviewClose(delayMs = 220): void {
    this.cancelHoverClose();
    this.hoverCloseTimer = setTimeout(() => {
      this.clearPreview();
      this.hoverCloseTimer = null;
    }, delayMs);
  }

  clearPreview(): void {
    if (this.previewRailId$.value !== null) {
      this.previewRailId$.next(null);
    }
  }

  /** Section id currently shown in the panel (preview overrides active). */
  getDisplayedRailId(): string | null {
    return this.previewRailId$.value ?? this.activeRailId$.value;
  }

  /** True when panel is showing a hover preview (not the committed section alone). */
  isHoverPreview(): boolean {
    const preview = this.previewRailId$.value;
    return !!preview && preview !== this.activeRailId$.value;
  }

  /**
   * Overview chips/lists only for the committed inline panel — never during hover flyout
   * or while peeking another section.
   */
  shouldLoadOverview(): boolean {
    const pinned = this.detailPanelPinned;
    const open = this.detailPanelOpen$.value;
    if (!pinned && !open) return false;

    const preview = this.previewRailId$.value;
    // Collapsed hover flyout: shortcuts only
    if (!pinned && preview) return false;
    // Pinned but peeking a different section: shortcuts only
    if (preview && preview !== this.activeRailId$.value) return false;

    return true;
  }

  setActiveRailId(id: string | null, persist = true): void {
    // Selecting a section must not leave a stale hover preview on another item.
    if (this.previewRailId$.value != null && this.previewRailId$.value !== id) {
      this.clearPreview();
    }
    this.activeRailId$.next(id);
    if (persist) {
      try {
        if (id) localStorage.setItem(STORAGE_RAIL_ID, id);
        else localStorage.removeItem(STORAGE_RAIL_ID);
      } catch {
        /* ignore */
      }
    }
  }

  setDetailPanelOpen(open: boolean, persist = true): void {
    this.detailPanelOpen$.next(open);
    if (persist) {
      this.persistPinned(open);
      this.detailPanelPinned = open;
    }
  }

  getActiveRailId(): string | null {
    return this.activeRailId$.value;
  }

  getPreviewRailId(): string | null {
    return this.previewRailId$.value;
  }

  isDetailPanelOpen(): boolean {
    return this.detailPanelOpen$.value;
  }

  /** @deprecated use toggleDetailPanel */
  collapseIconsMenu() {
    this.toggleDetailPanel();
  }

  /** @deprecated use isDetailPanelOpen — inverted: true meant icons-only (panel closed) */
  getCollapseStateIconsMenu() {
    return !this.detailPanelOpen$.value;
  }

  private cancelHoverClose(): void {
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }

  private persistPinned(pinned: boolean): void {
    try {
      localStorage.setItem(STORAGE_PANEL_PINNED, pinned ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  private readStoredRailId(): string | null {
    try {
      return localStorage.getItem(STORAGE_RAIL_ID);
    } catch {
      return null;
    }
  }

  private readStoredPinned(): boolean {
    try {
      return localStorage.getItem(STORAGE_PANEL_PINNED) === '1';
    } catch {
      return false;
    }
  }
}
